import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Flex } from 'components/Layout/Layout';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';

import {
  MUTATION_ADD_ANALYSE,
  MUTATION_EDIT_ANALYSE,
  QUERY_GET_ANALYSE,
  QUERY_GET_VIDEO_ANALYSES
} from '../../services/graphql/video-analyse-query';
import { ROUTE_ANALYSE_OVERVIEW, ROUTE_ANALYSE_INFO } from 'RouteList';

import ListIconButton from 'components/ListIconButton/ListIconButton';
import H2 from 'components/H2/H2';
import H3 from 'components/H3/H3';
import P from 'components/P/P';
import Button from 'components/Button/Button';
import { ANALYSE_BUILDING_BLOCKS, ANALYSE_TIME_RANGE } from '../../constants';
import messages from './messages';
import { useStores } from '../../store/useStores';

function Select({ videoAnalyseId, analyseId }) {
  const { routing, uiState, authStore } = useStores();
  const { isImpersonator, user } = authStore;

  const [maximumBlocks] = useState(2);
  const [selected, setSelected] = useState([]);
  const [, setShowMaximumBlocks] = useState(false);

  const hasMinimumBlocks = selected.length > 0;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleBlock = useCallback(
    id => {
      setSelected(prevSelected => {
        const next = [...prevSelected];
        const includes = next.includes(id);

        if (includes) {
          const index = next.indexOf(id);
          if (index !== -1) next.splice(index, 1);
          setShowMaximumBlocks(false);
          return next.sort((a, b) => a - b);
        }

        if (next.length < maximumBlocks) {
          next.push(id);
          setShowMaximumBlocks(false);
          return next.sort((a, b) => a - b);
        }

        setShowMaximumBlocks(true);
        return next;
      });
    },
    [maximumBlocks]
  );

  const intro = useMemo(() => {
    // preserve existing behavior: currently no UI uses showMaximumBlocks
    return null;
  }, []);

  const goBack = useCallback(() => {
    routing.push(`${ROUTE_ANALYSE_OVERVIEW}/${videoAnalyseId}`);
  }, [routing, videoAnalyseId]);

  const gotoInfoPage = useCallback(
    async mutationFn => {
      const buildingBlocks = [...selected].sort((a, b) => a - b);

      let response;
      try {
        response = await mutationFn({
          variables: {
            id: videoAnalyseId,
            videoAnalyseId,
            buildingBlocks,
            finished: false,
            timeRange: ANALYSE_TIME_RANGE
          }
        });
      } catch (err) {
        response = { error: err };
      }

      if (!response.error) {
        const newAnalyseId = analyseId
          ? analyseId
          : response.data?.addAnalyse?.id;
        routing.push(`${ROUTE_ANALYSE_INFO}/${videoAnalyseId}/${newAnalyseId}`);
      } else {
        uiState.showError(response.error.message);
      }
    },
    [analyseId, routing, selected, uiState, videoAnalyseId]
  );

  const { loading, error, data } = useQuery(QUERY_GET_ANALYSE, {
    variables: { analyseId },
    fetchPolicy: 'cache-and-network',
    skip: !analyseId
  });

  useEffect(() => {
    if (!analyseId) return;
    const buildingBlocks = data?.getAnalyse?.buildingBlocks || [];
    setSelected(buildingBlocks);
  }, [analyseId, data]);

  const refetchQueries = useMemo(
    () => [
      {
        query: QUERY_GET_VIDEO_ANALYSES,
        variables: {
          personId: isImpersonator ? isImpersonator.id : user.id
        }
      }
    ],
    [isImpersonator, user.id]
  );

  const [saveAnalyse, saveAnalyseState] = useMutation(
    analyseId ? MUTATION_EDIT_ANALYSE : MUTATION_ADD_ANALYSE,
    {
      refetchQueries
    }
  );

  if (loading) return <P>Bezig met laden...</P>;
  if (error) return <P danger>{error.message}</P>;

  return (
    <Flex flexWrap="wrap" style={{ textAlign: 'left' }} px={4}>
      {intro}
      <Box width={1} mt={4} mb={2}>
        <H2 upper>
          <FormattedMessage {...messages.analiseSelectTitle} />
        </H2>
      </Box>
      <Box width={1} mt={1} mb={4}>
        <FormattedMessage {...messages.analiseSelectIntro} />
      </Box>

      {ANALYSE_BUILDING_BLOCKS.map(block => (
        <BuildingBlockCategory
          key={block.id}
          title={block.title}
          data={block.data}
          toggleBlock={toggleBlock}
          selected={selected}
        />
      ))}

      <Box width={1} mt={2}>
        <Button primary onClick={goBack} style={{ marginRight: 8 }}>
          <FormattedMessage {...messages.analyseSelectBackButton} />
        </Button>

        <Button
          primary
          disabled={!hasMinimumBlocks || saveAnalyseState.loading}
          onClick={() => gotoInfoPage(saveAnalyse)}
        >
          <FormattedMessage {...messages.analiseSelectButton} />
        </Button>
      </Box>
    </Flex>
  );
}

const BuildingBlockCategory = ({ title = '', data, selected, toggleBlock }) => (
  <Flex width={1} flexWrap="wrap" mb={[2, 4]}>
    <Box width={1} mb={3}>
      <H3 upper textleft underlined lighter>
        {title}
      </H3>
    </Box>

    {data.map(block => (
      <BuildingBlockColumn
        key={block.id}
        title={block.title}
        items={block.items}
        toggleBlock={toggleBlock}
        selected={selected}
      />
    ))}
  </Flex>
);

const BuildingBlockColumn = ({
  title,
  selected = [],
  items = [],
  toggleBlock = id =>
    console.log('no toggleBlock function given to BuildingBlockColumn', id)
}) => (
  <Flex width={1 / 2} flexWrap="wrap" pr={4}>
    <Box width={1}>
      <Box mb={2}>
        <P upper bold>
          {title}
        </P>
      </Box>
      {items.map(dataItem => (
        <Box mb={2} key={dataItem.id} width={1}>
          <ListIconButton
            key={dataItem.id}
            statusColor={selected.includes(dataItem.id) ? 'success' : 'primary'}
            statusIcon={selected.includes(dataItem.id) ? 'check' : ''}
            label={dataItem.label}
            onClick={() => {
              toggleBlock(dataItem.id);
            }}
          />
        </Box>
      ))}
    </Box>
  </Flex>
);

export default observer(Select);
